/**
 * The brand mark, animated, for waits with nothing else on screen.
 *
 * ONE PLACE, DELIBERATELY. `App.tsx` renders this while the session resolves —
 * the whole application is booting, there is no layout to put a skeleton into,
 * and the alternative was the literal string "Loading…" alone. Everywhere else
 * keeps what it has: `SkeletonRows` for a table, because a placeholder in the
 * shape of the data is better than a symbol; `Spinner` for a button, because it
 * inherits the button's colour and fits on one line. A logo animation in either
 * of those would be worse than none.
 *
 * DECORATIVE, AND THE CALLER SUPPLIES THE WORDS. `Spinner` can carry its own
 * accessible name because it is often the only thing in its container; this one
 * sits beside real text on a screen holding nothing else, so naming it too
 * would announce the wait twice. `aria-hidden` and a visible label is also the
 * more robust half of the choice: `role="status"` on an element whose content
 * never changes is a live region with nothing to announce, and support for
 * reading its `aria-label` instead is uneven.
 *
 * ITS OWN COPY OF THE PATHS, NOT `<use href="#oc-logo-mark">`. The sprite is a
 * `<symbol>` inlined once by `Sprites.tsx` and referenced by `<use>`, and the
 * referenced content lives in a shadow tree that ordinary CSS selectors cannot
 * reach — `.oc-loading-mark use circle` matches nothing. Animating individual
 * nodes therefore requires the geometry to be in this document. The duplication
 * is real and is the cost of the animation; the colours still come from tokens,
 * so the mark follows the palette exactly as the sprite does.
 *
 * WHY THE WHOLE THING DOES NOT ROTATE. The symbol is a connected network with a
 * pulse through it: seven nodes joined by a *partial* set of strokes, with an
 * ECG-style trace across the middle. Spinning it tumbles that waveform, and a
 * trace at ninety degrees reads as broken rather than busy — while the
 * asymmetric connections visibly wobble as they come round. The highlight
 * travels the ring instead, so the motion is rotational and the mark stays
 * upright.
 */

export function LoadingMark({ size = 96 }: { size?: number }) {
  return (
    <svg
      className="oc-loading-mark"
      width={size}
      height={size}
      viewBox="0 0 260 260"
      aria-hidden="true"
      focusable="false"
    >
      <g className="oc-loading-mark__net" fill="none" strokeLinecap="square" strokeWidth="7">
        <path d="M120 31V84L178 142" />
        <path d="M205 72L162 112" />
        <path d="M231 156H182L151 205" />
        <path d="M174 232L130 188" />
        <path d="M82 232L105 194" />
        <path d="M29 154H77L101 119" />
        <path d="M47 72L83 105" />
      </g>
      <g
        className="oc-loading-mark__net oc-loading-mark__net--faint"
        fill="none"
        strokeLinecap="square"
        strokeWidth="7"
      >
        <path d="M120 31L205 72" />
        <path d="M29 154L82 232" />
      </g>

      <path
        className="oc-loading-mark__wave"
        d="M29 154H72L89 154L101 132L112 177L128 93L143 177L158 137L170 154H231"
        fill="none"
        strokeLinecap="square"
        strokeWidth="9"
      />

      {/* Document order is ring order — clockwise from the top — which is what
          lets the stagger be a plain nth-child in CSS rather than seven
          hand-written delays that drift when somebody reorders the markup. */}
      <g strokeWidth="7">
        <circle className="oc-loading-mark__node" cx="120" cy="18" r="13" />
        <circle className="oc-loading-mark__node" cx="210" cy="62" r="13" />
        <circle className="oc-loading-mark__node" cx="238" cy="156" r="13" />
        <circle className="oc-loading-mark__node" cx="180" cy="236" r="13" />
        <circle className="oc-loading-mark__node" cx="74" cy="236" r="13" />
        <circle className="oc-loading-mark__node" cx="20" cy="154" r="13" />
        <circle className="oc-loading-mark__node" cx="42" cy="70" r="13" />
      </g>

      <circle className="oc-loading-mark__cap" cx="29" cy="154" r="7" />
      <circle className="oc-loading-mark__cap" cx="231" cy="156" r="7" />
      <path className="oc-loading-mark__stem" d="M120 18V42" strokeWidth="7" />
    </svg>
  );
}
